import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Loader2, GraduationCap } from "lucide-react";

interface EnrollmentDataPoint {
  course: string;
  total: number;
  accepted: number;
}

interface EnrollmentsByCourseChartProps {
  data: EnrollmentDataPoint[];
  isLoading: boolean;
}

const COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--accent))",
  "hsl(168, 60%, 45%)",
  "hsl(262, 50%, 55%)",
  "hsl(35, 85%, 55%)",
  "hsl(340, 60%, 55%)",
];

export default function EnrollmentsByCourseChart({ data, isLoading }: EnrollmentsByCourseChartProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.22 }}
      className="bg-card rounded-2xl p-5 border border-border mb-8"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center">
          <GraduationCap className="w-4 h-4" />
        </div>
        <h3 className="font-heading font-semibold text-foreground">Matrículas por Curso</h3>
      </div>

      <div className="h-64">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
            Nenhuma matrícula encontrada
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis
                dataKey="course"
                type="category"
                width={140}
                tick={{ fontSize: 11 }}
                stroke="hsl(var(--muted-foreground))"
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
                labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 600 }}
                formatter={(value: number, name: string) => [
                  value,
                  name === "total" ? "Total" : "Aceitos",
                ]}
              />
              <Bar dataKey="total" name="total" radius={[0, 4, 4, 0]} barSize={18} opacity={0.35}>
                {data.map((_, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
              <Bar dataKey="accepted" name="accepted" radius={[0, 4, 4, 0]} barSize={18}>
                {data.map((_, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-2 rounded-sm bg-primary/35" /> Total
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-2 rounded-sm bg-primary" /> Aceitos
        </span>
      </div>
    </motion.div>
  );
}
